import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useMSRNudgeRecommendations } from '@atlassian/cross-join-nudges';

import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useFireOnlyNewLoomEvent } from '../use-fire-only-new-loom-event/index.tsx';

export const useCrossJoinNudges = ({ flags }: { flags: FlagsStateContainer }) => {
	const { formatMessage } = useIntl();
	const { cloudId, userId, displayName } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireOnlyNewLoomEvent = useFireOnlyNewLoomEvent();
	const { push } = useRouteActions();
	const getMSRNudgeRecommendations = useMSRNudgeRecommendations({
		product: 'confluence',
		sessionId: uuid(),
		cloudId,
		subProduct: '',
	});

	useEffect(() => {
		const execCrossJoinNudge = async () => {
			if (userId) {
				const { checkLoomCrossJoinNudgeEligibility, showLoomCrossJoinNudge } = await import(
					/* webpackChunkName: "loadable-loom-cross-join-nudges-services" */ '../../services/loom-cross-join-nudge.ts'
				);

				const isLoomCrossJoinNudgesEligible = await checkLoomCrossJoinNudgeEligibility({
					cloudId,
					userId,
					createAnalyticsEvent,
				});

				if (isLoomCrossJoinNudgesEligible.eligible) {
					await showLoomCrossJoinNudge({
						cloudId,
						userId,
						displayName,
						flags,
						createAnalyticsEvent,
						formatMessage,
					});
					return;
				}

				// Track eligibility failure reasons
				fireOnlyNewLoomEvent({ value: isLoomCrossJoinNudgesEligible, key: 'cjn-conf-loom' });
			}

			const {
				checkJpdCrossJoinNudgeEligibility,
				showJpdCrossJoinNudge,
				triggerJiraCrossJoinNudge,
			} = await import(
				/* webpackChunkName: "loadable-cross-join-nudges-services" */ '../../services/run-cross-join-nudges.ts'
			);

			const isJpdCrossJoinNudgesEligible = await checkJpdCrossJoinNudgeEligibility({
				cloudId,
				userId,
				createAnalyticsEvent,
				getMSRNudgeRecommendations,
			});

			if (isJpdCrossJoinNudgesEligible) {
				await showJpdCrossJoinNudge({
					cloudId,
					userId,
					displayName,
					flags,
					createAnalyticsEvent,
					push,
					formatMessage,
				});
			} else {
				await triggerJiraCrossJoinNudge({
					cloudId,
					userId,
					displayName,
					flags,
					createAnalyticsEvent,
					push,
					formatMessage,
					getMSRNudgeRecommendations,
				});
			}
		};

		void execCrossJoinNudge();
		// Disabling as we only want this to execute based on `execCrossJoinNudge` or if `flags` gets updated.
		// This prevents edge cases where flags might be shown twice due to
		// Complex dependency arrays of children components/dependencies
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags]);
};

export const CrossJoinNudgesWithFlags = withFlags(({ flags }: { flags: FlagsStateContainer }) => {
	useCrossJoinNudges({ flags });
	return null;
});
